import { useState } from 'react';
import { useCdrDetail } from '../../lib/api/hooks.js';
import { useCan, useOwnOnly } from '../../lib/auth/session.js';
import { Badge, ErrorBanner, Segmented, Stat, StatGroup } from '../../components/ui/Primitives.js';
import { Modal } from '../../components/ui/Modal.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { formatDuration, formatMoney, formatDateTime } from '../../lib/format.js';
import { RecordingPlayer } from './RecordingPlayer.js';

/**
 * One call, opened from its row in the history, in a window of its own.
 *
 * It opened under its row, pushing the list apart; the owner wanted a window instead, one with
 * room for the recording drawn as a waveform and for what comes later -- the transcript, the
 * quality review (22 September 2026). No address of its own: a call is known only by a uuid, and
 * uuids do not go in addresses. Escape or the backdrop closes it.
 *
 * What most calls are opened for -- when, who, how long, what it cost, what it sounded like -- is
 * the first tab. Why it ended, what the network did, what it was rated against and the other legs
 * are a tab each, and a tab with nothing to say is not offered. Every group drops rows with
 * nothing in them: a panel of dashes reads as a broken screen.
 */
type Tab = 'overview' | 'details' | 'legs' | 'channel';

const DIRECTION: Record<string, { icon: IconName; label: string; tone: string }> = {
  inbound: { icon: 'callIn', label: 'Inbound call', tone: 'var(--arc-dir-in)' },
  outbound: { icon: 'callOut', label: 'Outbound call', tone: 'var(--arc-dir-out)' },
  internal: { icon: 'callInternal', label: 'Internal call', tone: 'var(--arc-fg-4)' },
};

/** Why it ended, in words, for a call that was not answered. */
const OUTCOME: Record<string, string> = {
  NO_ANSWER: 'No answer', NO_USER_RESPONSE: 'No answer', USER_BUSY: 'Busy',
  ORIGINATOR_CANCEL: 'Caller hung up', CALL_REJECTED: 'Rejected',
  UNALLOCATED_NUMBER: 'Number not in use', NO_ROUTE_DESTINATION: 'No route',
  USER_NOT_REGISTERED: 'Phone offline', SUBSCRIBER_ABSENT: 'Phone offline',
  NORMAL_TEMPORARY_FAILURE: 'Network failure', RECOVERY_ON_TIMER_EXPIRE: 'Timed out',
};

/**
 * A number as a person reads it. A seat's SIP login (1002-meridian) is its extension; a South
 * African national number is grouped 3-3-4; anything else is left as it came.
 */
export function readableNumber(n: string | null | undefined): string {
  if (!n) return 'Unknown';
  const seat = /^(\d{2,6})-[a-z0-9.]+$/i.exec(n);
  if (seat) return seat[1]!;
  if (/^0\d{9}$/.test(n)) return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
  const za = /^\+?27(\d{9})$/.exec(n);
  if (za) return `+27 ${za[1]!.slice(0, 2)} ${za[1]!.slice(2, 5)} ${za[1]!.slice(5)}`;
  return n;
}

export function CallModal({ tenantId, callId, tz, onClose }: {
  tenantId: string; callId: string; tz: string; onClose: () => void;
}) {
  const q = useCdrDetail(tenantId, callId);
  const [tab, setTab] = useState<Tab>('overview');
  const mayHear = useCan()('recording:read');
  const ownOnly = useOwnOnly();
  const c = q.data;

  const d = c ? DIRECTION[c.direction] ?? DIRECTION['internal']! : null;
  const title = c && d
    ? (
      <span className="arc-row" style={{ gap: 8 }}>
        <span style={{ color: d.tone, display: 'inline-flex' }}><Icon name={d.icon} size={16} /></span>
        {d.label}
        <span className="arc-subtle" style={{ fontWeight: 400 }}>· {formatDateTime(c.started_at, tz)}</span>
      </span>
    )
    : 'Call';

  return (
    <Modal title={title} onClose={onClose}>
      <div className="arc-call">
        {q.error ? <ErrorBanner error={q.error} />
          : !c ? <div className="arc-skeleton" style={{ height: 260 }} />
            : <CallBody c={c} tenantId={tenantId} tz={tz} tab={tab} setTab={setTab}
                mayHear={mayHear || ownOnly} />}
      </div>
    </Modal>
  );
}

function CallBody({ c, tenantId, tz, tab, setTab, mayHear }: {
  c: NonNullable<ReturnType<typeof useCdrDetail>['data']>;
  tenantId: string; tz: string; tab: Tab; setTab: (t: Tab) => void; mayHear: boolean;
}) {
  const num = (v: string | null, unit: string): string | null =>
    v === null ? null : `${Number(v).toFixed(unit === 'ms' ? 1 : 2)} ${unit}`;

  const summary: Group[] = [
    ['Call', [
      ['Started', formatDateTime(c.started_at, tz)],
      ['Answered', c.answered_at ? formatDateTime(c.answered_at, tz) : 'never answered'],
      ['Ended', formatDateTime(c.ended_at, tz)],
      ['Talk time', c.answered_at ? formatDuration(c.duration_sec) : null],
      ['Charged', c.sell_amount === null ? null : formatMoney(c.sell_amount, c.currency)],
      // Rated twice and disagreed: worth seeing on the call itself.
      ['Rating', c.rating_mismatch ? <Badge tone="warn">node and platform disagreed</Badge> : null],
    ]],
    ['Parties', [
      // A caller with no name comes through with the number as the name.
      ['From', c.from_name && c.from_name !== c.from_number
        ? `${c.from_name} · ${c.from_number ?? ''}` : c.from_number],
      ['To', c.to_number],
      ['Extension', c.extension_number],
    ]],
  ];

  const details: Group[] = [
    ['Signalling', [
      // The cause answers "why did this call do that"; the SIP code is what a carrier quotes back.
      ['Hangup cause', c.hangup_cause ? <span className="arc-mono">{c.hangup_cause}</span> : null],
      ['SIP response', c.sip_code ? <span className="arc-mono">{c.sip_code}</span> : null],
      ['Dialled', c.destination_e164 !== c.to_number ? c.destination_e164 : null],
      ['Carrier', c.trunk_name ?? null],
    ]],
    ['Network', [
      ['Codec', c.codec ? <span className="arc-mono">{c.codec}</span> : null],
      ['MOS', c.mos],
      ['Jitter', num(c.jitter_ms, 'ms')],
      ['Packet loss', c.packet_loss_pct === null ? null : `${Number(c.packet_loss_pct).toFixed(2)} %`],
      ['Round trip', num(c.rtt_ms, 'ms')],
    ]],
    ['Billing', [
      ['Billable', c.is_billable_leg ? formatDuration(c.billable_sec)
        : c.on_own_trunk && c.leg === 'a' ? 'not charged, own trunk' : 'not the billable leg'],
      ['Cost', c.cost_amount === undefined || c.cost_amount === null ? null : formatMoney(c.cost_amount, c.currency)],
      ['Margin', c.margin_amount === undefined || c.margin_amount === null ? null : formatMoney(c.margin_amount, c.currency)],
      ['Rated on node', c.rated_on_node === undefined || c.rated_on_node === null
        ? null : formatMoney(c.rated_on_node, c.currency)],
      ['Rate rule', c.rate_rule_id ? <span className="arc-mono">{c.rate_rule_id.slice(0, 8)}</span> : null],
    ]],
    ['Recording', [
      ['Format', c.recording ? (c.recording.format.startsWith('mp3') ? `MP3, ${c.recording.format.slice(4)}` : 'WAV') : null],
      ['Size', c.recording?.bytes ? `${Math.round(c.recording.bytes / 1024)} KB` : null],
      // Kept so a recording can be shown to be the one that was made (0096, 0097).
      ['Stored file SHA-256', c.recording?.sha256 ? <span className="arc-mono" style={{ wordBreak: 'break-all' }}>{c.recording.sha256}</span> : null],
      ['As recorded SHA-256', c.recording?.source_sha256 && c.recording.source_sha256 !== c.recording.sha256
        ? <span className="arc-mono" style={{ wordBreak: 'break-all' }}>{c.recording.source_sha256}</span> : null],
    ]],
  ];

  const tabs: Array<readonly [Tab, string]> = [
    ['overview', 'Overview'],
    ['details', 'Details'],
    ...(c.legs.length > 1 ? [['legs', 'Legs'] as const] : []),
    // Staff only: the switch's own plumbing, useful on the day a call went wrong.
    ...(c.variables && Object.keys(c.variables).length > 0 ? [['channel', 'Channel'] as const] : []),
  ];

  const realName = (n: string | null | undefined): string | null =>
    n && !/^[\d+\s]+$/.test(n) && !/^\d{2,6}-[a-z0-9.]+$/i.test(n) ? n : null;
  const fromName = realName(c.from_name) ?? readableNumber(c.from_number);
  const fromSub = c.direction !== 'inbound' && c.extension_number
    ? `Extension ${c.extension_number}`
    : realName(c.from_name) ? readableNumber(c.from_number) : 'Outside number';
  const toName = readableNumber(c.to_number ?? c.destination_e164);
  const toSub = c.direction === 'inbound' && c.extension_number
    ? `Answered at ${c.extension_number}`
    : c.direction === 'internal' ? 'Extension' : c.trunk_name ? `Via ${c.trunk_name}` : 'Outside number';
  const outcome = c.answered_at ? 'Answered'
    : OUTCOME[c.hangup_cause ?? ''] ?? (c.hangup_cause ? c.hangup_cause.toLowerCase().replace(/_/g, ' ') : 'Not answered');
  const mos = c.mos ? Number(c.mos) : null;
  const mosLabel = mos === null ? null : mos >= 4 ? 'good' : mos >= 3.5 ? 'fair' : 'poor';
  const mosTone = mos === null ? undefined : mos >= 4 ? 'ok' as const : mos >= 3.5 ? 'warn' as const : 'danger' as const;

  // The left channel is the leg the recording started on, and that leg's own party.
  const laneNames: [string, string] = [fromName, toName];

  return (
    <div className="arc-stack" style={{ gap: 16 }}>
      <Segmented label="Call sections" value={tab} options={tabs} onChange={setTab} />

      {tab === 'overview' && (
        <>
          <div className="arc-call-parties">
            <Party role="From" name={fromName} sub={fromSub} />
            <span className="arc-call-arrow" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
            <Party role="To" name={toName} sub={toSub} />
          </div>

          <div className="arc-card arc-call-stats">
            <StatGroup>
              <Stat bare label="Talk time" value={c.answered_at ? formatDuration(c.duration_sec) : '—'} />
              <Stat bare label="Result" value={outcome}
                tone={c.answered_at ? undefined : 'warn'} />
              <Stat bare label="Charged"
                value={c.sell_amount === null ? '—' : formatMoney(c.sell_amount, c.currency)}
                sub={c.rating_mismatch ? 'node and platform disagreed' : undefined} />
              <Stat bare label="Quality" value={c.mos ? Number(c.mos).toFixed(2) : '—'}
                sub={mosLabel ?? undefined}
                tone={mosTone} />
            </StatGroup>
          </div>

          {/* What the agent said it was, and their note (0108). Above the recording, because it
              is the answer somebody opening a call is usually looking for -- the recording is
              what they reach for when it does not explain itself. */}
          {c.disposition && (
            <div className="arc-card arc-card-body arc-call-disp" data-kind={c.disposition.kind}>
              <div className="arc-row" style={{ gap: 8 }}>
                <span className="arc-pause-chip arc-disp" data-kind={c.disposition.kind}>
                  {c.disposition.label}
                </span>
                <span className="arc-subtle" style={{ fontSize: 11.5 }}>marked by the agent</span>
              </div>
              {c.disposition.note && (
                <p style={{ margin: '8px 0 0', fontSize: 12.5 }}>{c.disposition.note}</p>
              )}
            </div>
          )}

          {/* Played under recording:read, or a person's own calls (permission sweep, 19 September 2026). */}
          {c.has_recording && mayHear && (
            <RecordingPlayer tenantId={tenantId} callId={c.id} status={c.recording_status ?? 'ready'}
              laneNames={laneNames} expectedSha256={c.recording?.sha256 ?? null}
              format={c.recording?.format ?? null} bytes={c.recording?.bytes ?? null} />
          )}

          {c.answered_at === null && <Groups groups={summary} />}
        </>
      )}

      {tab === 'details' && <Groups groups={details} />}

      {/*
        Only when there IS another leg. An outbound call is the seat's leg and the carrier's, and
        which of the two the list showed is an accident of sorting -- so the whole call is shown.
      */}
      {tab === 'legs' && (
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Leg</th><th>Direction</th><th>From</th><th>To</th>
                <th className="arc-td-right">Duration</th><th>Ended</th>
              </tr>
            </thead>
            <tbody>
              {c.legs.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.leg === 'a' ? 'caller' : 'carrier'}
                    {l.id === c.id && <span className="arc-subtle"> · this one</span>}
                  </td>
                  <td>{l.direction}</td>
                  <td className="arc-mono">{l.from_number ?? '—'}</td>
                  <td className="arc-mono">{l.to_number ?? '—'}</td>
                  <td className="arc-td-right arc-num">{formatDuration(l.duration_sec)}</td>
                  <td className="arc-mono arc-subtle">
                    {l.hangup_cause ?? '—'}{l.sip_code ? ` · ${l.sip_code}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'channel' && c.variables && (
        <pre className="arc-mono" style={{
          margin: 0, padding: 10, fontSize: 11.5, lineHeight: 1.5,
          background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)',
          maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>{JSON.stringify(c.variables, null, 2)}</pre>
      )}
    </div>
  );
}

type Row = [string, React.ReactNode];
type Group = [string, Row[]];

/** Rows with nothing in them are dropped, not rendered as a dash, and so is a group left empty. */
function Groups({ groups }: { groups: Group[] }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16,
    }}>
      {groups.map(([title, rows]) => {
        const present = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
        if (present.length === 0) return null;
        return (
          <div key={title}>
            <div className="arc-label" style={{ marginBottom: 6 }}>{title}</div>
            <dl className="arc-kv">
              {present.map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt><dd>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

/** One side of the call: who, and what they are to this account. */
function Party({ role, name, sub }: { role: string; name: string; sub: string }) {
  return (
    <div className="arc-call-party">
      <div className="arc-stat-label">{role}</div>
      <div className="arc-call-party-name">{name}</div>
      <div className="arc-call-party-sub">{sub}</div>
    </div>
  );
}
